import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Check, Copy, CreditCard, Download, Gauge, Loader2, Network } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { hasCapability } from '../../../permissions';
import { ErrorState } from '../../shared/components/ErrorState';
import { EnterpriseStatusBadge } from '../../shared/components/EnterpriseStatusBadge';
import { ModuleCard, ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import { normalizeOrganizationStatus } from '../utils/normalizeOrganizationStatus';
import { exportOrganizationData, downloadBackup } from '../../../services/backupService';

interface OrganizationSnapshot {
  name: string;
  createdAt: string | null;
  activeUsers: number;
  plan: string | null;
  billingStatus: string | null;
}

export const OrganizationPage: React.FC = () => {
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const organizationId = workspace?.organizationId;
  const [isExporting, setIsExporting] = useState(false);

  const canViewStructure = hasCapability(
    workspace?.userRole ? { role: workspace.userRole } : null,
    'org_structure.view',
    { realRoleOnly: true },
  );

  const handleBackup = async () => {
    if (!organizationId) return;
    setIsExporting(true);
    try {
      const backup = await exportOrganizationData(organizationId);
      downloadBackup(backup);
    } catch (err) {
      console.error('Falha ao exportar backup:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const orgQuery = useQuery({
    queryKey: ['organization-page', organizationId],
    enabled: Boolean(organizationId),
    queryFn: async (): Promise<OrganizationSnapshot> => {
      if (!organizationId) {
        return {
          name: workspace?.organizationName || 'Organização',
          createdAt: null,
          activeUsers: 0,
          plan: null,
          billingStatus: null,
        };
      }

      const [orgResult, usersResult, licenseResult] = await Promise.all([
        supabase.from('organizations').select('id, name, created_at').eq('id', organizationId).maybeSingle(),
        supabase
          .from('user_organizations')
          .select('user_id', { count: 'exact', head: true })
          .eq('organization_id', organizationId),
        supabase
          .from('client_licenses')
          .select('plan, status')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      if (orgResult.error) throw orgResult.error;
      if (usersResult.error) throw usersResult.error;
      if (licenseResult.error) throw licenseResult.error;

      return {
        name: orgResult.data?.name ?? workspace?.organizationName ?? 'Organização',
        createdAt: orgResult.data?.created_at ?? null,
        activeUsers: usersResult.count ?? 0,
        plan: licenseResult.data?.plan ? String(licenseResult.data.plan) : null,
        billingStatus: licenseResult.data?.status ? String(licenseResult.data.status) : null,
      };
    },
  });

  const normalizedStatus = normalizeOrganizationStatus({ status: orgQuery.data?.billingStatus });
  const normalizedStatusTone =
    normalizedStatus === 'ACTIVE'
      ? 'active'
      : normalizedStatus === 'TRIAL'
        ? 'warning'
        : normalizedStatus === 'SUSPENDED' || normalizedStatus === 'CANCELED'
          ? 'risk'
          : 'draft';

  return (
    <div className="enterprise-page h-full overflow-y-auto">

      {orgQuery.isPending && <ModuleLoadingState label="Carregando dados da organização" />}

      {orgQuery.isError && (
        <ErrorState message="Não foi possível carregar os dados da organização." onRetry={orgQuery.refetch} />
      )}

      {!orgQuery.isPending && !orgQuery.isError && (
        <div className="space-y-[var(--block-gap)]">
          {/* ─── Grid principal: Identidade Organizacional ──────────────────── */}
          <div className="grid grid-cols-12 gap-[var(--block-gap)]">
            <div className="col-span-12">
              <ModuleCard title="Identidade Organizacional">
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-[var(--block-gap)] text-sm">
                  <DataRow label="Nome da organização" value={orgQuery.data?.name || workspace?.organizationName || '-'} />
                  <DataRow label="Plano atual" value={
                    <span className="inline-flex items-center gap-2">
                      <span className="font-mono text-xs">{orgQuery.data?.plan ? orgQuery.data.plan.toUpperCase() : 'Não configurado'}</span>
                      <button
                        type="button"
                        onClick={() => navigate('/org/billing')}
                        className="enterprise-button-secondary inline-flex items-center gap-1 px-2 py-0.5 text-xs"
                      >
                        <CreditCard size={12} />
                        Gerenciar Plano
                      </button>
                    </span>
                  } />
                  <DataRow label="Usuarios ativos" value={String(orgQuery.data?.activeUsers ?? 0)} mono />
                  <DataRow label="Status" value={<EnterpriseStatusBadge label={normalizedStatus} tone={normalizedStatusTone} />} />
                  <DataRow label="Data de criação"
                    value={orgQuery.data?.createdAt ? new Date(orgQuery.data.createdAt).toLocaleDateString('pt-BR') : 'Não informado'}
                  />
                  <DataRow label="ID da organização" value={organizationId || '-'} mono copyable />
                </dl>
              </ModuleCard>
            </div>
          </div>

          {/* ─── Seção secundária: Backup + Estrutura (2 colunas) ───────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--block-gap)]">
            <ModuleCard title="Backup de Dados">
              <p className="text-xs enterprise-text-secondary mb-3">
                Exporte todos os dados da organização em JSON (LGPD Art. 18).
              </p>
              <button
                type="button"
                onClick={handleBackup}
                disabled={isExporting || !organizationId}
                className="enterprise-button-secondary inline-flex items-center gap-2 px-4 py-2 text-sm font-medium w-full justify-center"
              >
                {isExporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                {isExporting ? 'Exportando...' : 'Exportar Backup'}
              </button>
            </ModuleCard>

            <ModuleCard title="Capacidade e Demanda">
              <p className="text-xs enterprise-text-secondary mb-3">
                Demanda planejada em tarefas/h para a análise de capacidade. Não é WIP nem throughput.
              </p>
              <button
                type="button"
                onClick={() => navigate('/org/capacidade')}
                className="enterprise-button-secondary inline-flex items-center gap-2 px-4 py-2 text-sm font-medium w-full justify-center"
              >
                <Gauge size={14} />
                Configurar demanda planejada
              </button>
            </ModuleCard>

            {canViewStructure && (
              <ModuleCard title="Estrutura Organizacional">
                <p className="text-xs enterprise-text-secondary mb-3">
                  Organograma único: cargo, departamento e gestor direto de cada membro.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/org/estrutura')}
                  className="enterprise-button-secondary inline-flex items-center gap-2 px-4 py-2 text-sm font-medium w-full justify-center"
                >
                  <Network size={14} />
                  Configurar Vínculos e Cargos
                </button>
              </ModuleCard>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DataRow: React.FC<{ label: string; value: React.ReactNode; mono?: boolean; copyable?: boolean }> = ({ label, value, mono = false, copyable = false }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    if (typeof value === 'string') {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  return (
    <div className="enterprise-card-surface p-[var(--card-padding)]">
      <dt className="enterprise-field-label">{label}</dt>
      <dd className={`mt-1 text-base font-semibold enterprise-text-primary flex items-center gap-2 ${mono ? 'font-mono text-[12px]' : ''}`}>
        {value}
        {copyable && typeof value === 'string' && (
          <button type="button" onClick={handleCopy} className="enterprise-text-secondary hover:enterprise-text-primary transition-colors" title="Copiar">
            {copied ? <Check size={13} /> : <Copy size={13} />}
          </button>
        )}
      </dd>
    </div>
  );
};
